import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { createAuthGuard } from '@inoventoryapps/keycloak-angular';
import { APPLICATION_ROLES } from '../../constants/application-roles.constant';
import { EXCLUDED_RESOURCES } from '../../constants/excluded-resources.constant';
import { LOCAL_STORAGE_KEYS } from '../../enums/local-storage.enum';
import { Roles } from '../../enums/roles.enum';
import { AppInitializationService } from '../../services/app-initialization/app-initialization.service';
import { mobileBridge } from '../../services/mobile/mobile-bridge';
import { SnackbarService } from '../../services/snackbar/snackbar.service';
import { StorageService } from '../../services/storage/storage-service';

const unauthorizedUrl = (): string =>
    mobileBridge.isPWA() ? '/mobile/unauthorized-access' : '/web/unauthorized-access';

export const AuthKeycloakGuard = createAuthGuard(
    async (route: ActivatedRouteSnapshot, state: RouterStateSnapshot, authData) => {
        const appInitializationService = inject(AppInitializationService);
        const router = inject(Router);
        const snackbarService = inject(SnackbarService);
        const storageService = inject(StorageService);
        const { authenticated, grantedRoles } = authData;

        const isUnauthorizedPage = state.url.endsWith('/unauthorized-access');

        if (!authenticated) {
            const profile = await mobileBridge.getUserProfile();
            const hasNativeTokens = !!profile?.tokens;

            if (mobileBridge.isPWA() && hasNativeTokens) {
                if (isUnauthorizedPage) {
                    return true;
                }

                snackbarService.errorSnackbar('Session unavailable. Please reopen this page from the app.');
                return router.createUrlTree([unauthorizedUrl()]);
            }

            await authData.keycloak.login({ idpHint: '' }).catch(() => {
                snackbarService.errorSnackbar('Login failed. Please try again.');
            });
            return false;
        }

        if (isUnauthorizedPage) {
            return true;
        }

        await appInitializationService.initializeAppData();

        const routeRoles = (route.data['allowedRoles'] as string[] | undefined) ?? [];

        if (routeRoles.includes('all') || routeRoles.includes(Roles.All)) {
            return true;
        }

        // ── Mobile (PWA) path ────────────────────────────────────────────────────
        if (mobileBridge.isPWA()) {
            const bridgeRole = mobileBridge.getRole() as Roles;
            const userRoles = storageService.getLocalItem<string[]>(LOCAL_STORAGE_KEYS.USER_ROLES) ?? [];
            const mobileApplicationRoles = [
                Roles.SHG_BOOKKEEPER,
                Roles.VO_BOOKKEEPER,
                Roles.CLF_BOOKKEEPER,
                Roles.UDYOG_MITRA,
                Roles.BCF,
                Roles.BCIB,
                Roles.BCNF,
            ];

            const hasMobileAppAccess =
                userRoles.some((role) => mobileApplicationRoles.includes(role as Roles)) ||
                mobileApplicationRoles.includes(bridgeRole);

            if (!hasMobileAppAccess) {
                snackbarService.errorSnackbar('You do not have access to this application');
                return router.createUrlTree([unauthorizedUrl()]);
            }

            const isRouteAllowed =
                routeRoles.includes(bridgeRole) || userRoles.some((role) => routeRoles.includes(role));

            if (isRouteAllowed) {
                return true;
            }

            snackbarService.errorSnackbar('You do not have permission to access this page');
            return router.createUrlTree([unauthorizedUrl()]);
        }

        // ── Web path ─────────────────────────────────────────────────────────────
        const userRoles = [
            ...grantedRoles.realmRoles,
            ...Object.entries(grantedRoles.resourceRoles)
                .filter(([resource]) => !EXCLUDED_RESOURCES.has(resource))
                .flatMap(([, roles]) => roles),
        ];

        const activeRole = storageService.getLocalItem<Roles>(LOCAL_STORAGE_KEYS.ACTIVE_ROLE);

        // Stage 1 — Application access: the user must hold at least one APPLICATION_ROLE.
        const hasApplicationAccess = userRoles.some((role) => APPLICATION_ROLES.includes(role as Roles));

        if (!hasApplicationAccess) {
            snackbarService.errorSnackbar('You do not have access to this application');
            return router.createUrlTree([unauthorizedUrl()]);
        }

        // Stage 2 — Route access by the active role
        if (activeRole) {
            const isRouteAllowed = userRoles.includes(activeRole) && routeRoles.includes(activeRole);

            if (isRouteAllowed) {
                return true;
            }

            snackbarService.errorSnackbar('You do not have permission to access this page');
            return router.createUrlTree([unauthorizedUrl()]);
        }

        // Fallback to full role-set check if no active role is selected yet
        const hasPermission = routeRoles.some((role) => userRoles.includes(role));

        if (hasPermission) {
            return true;
        }

        snackbarService.errorSnackbar('You do not have permission to access this page');
        return router.createUrlTree([unauthorizedUrl()]);
    },
);
